import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";

// Connects to data-controller="audio-message"
export default class extends Controller {
  static targets = [
    "micBtn",
    "sendBtn",
    "audioControls",
    "textInput",
    "fileInput",
    "timer",
    "visualizer",
  ];

  connect() {
    // Initialize states
    this.audioCtx = null;
    this.stream = null;
    this.visualizerAnimationId = null;
    this.mediaRecorder = null;
    this.chunks = [];
    this.isCancelled = false;
  }

  disconnect() {
    this.cleanup();
  }

  // --- 1. UI TOGGLES ---
  toggleSubmitBtn() {
    // Show Send button only if text input is not empty
    const isEmpty = this.textInputTarget.value.trim() === "";
    if (isEmpty) {
      this.micBtnTarget.classList.remove("hidden");
      this.sendBtnTarget.classList.add("hidden");
    } else {
      this.micBtnTarget.classList.add("hidden");
      this.sendBtnTarget.classList.remove("hidden");
    }
  }

  // --- 2. RECORDING FLOW ---
  startRecording(event) {
    event.preventDefault();

    // alert("Is Android Bridge attached? " + !!window.AudioNative);

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      alert("Audio recording not supported. Ensure strict HTTPS.");
      return;
    }

    // A. Native App Flow (Android)
    if (window.AudioNative) {
      // Define what happens when Android finishes asking the user
      window.onAudioPermissionResult = async (isGranted) => {
        if (isGranted) {
          await this.executeGetUserMedia();
        } else {
          alert("Microphone permission was denied by the OS.");
        }
      };

      // Tell Android to check/ask for OS permissions FIRST
      window.AudioNative.checkAndRequestPermission();
    }
    // B. Standard Web Browser Flow
    else {
      this.executeGetUserMedia();
    }
  }

  async executeGetUserMedia() {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });

      // Update UI now that hardware is successfully connected
      this.textInputTarget.value = "";
      this.toggleSubmitBtn();
      this.setupRecorder(this.stream);
      this.startTimer();
      this.audioControlsTarget.classList.remove("hidden");
      this.textInputTarget.disabled = true;
      this.visualize(this.stream);
    } catch (err) {
      alert(`Mic Blocked: ${err.name} - ${err.message}.`);
      console.error("Error accessing microphone:", err);
    }
  }

  stopRecording(event) {
    if (event) event.preventDefault();

    // Stop recorder if active; onstop triggers upload
    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      this.mediaRecorder.stop();
    }

    this.cleanup();
    this.resetUI();
  }

  deleteRecording(event) {
    event.preventDefault();
    this.isCancelled = true; // Set flag to prevent upload in onstop

    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      this.mediaRecorder.stop();
    }
    this.cleanup();
    this.resetUI();
  }

  // --- 3. MEDIA RECORDER SETUP ---
  setupRecorder(stream) {
    this.mediaRecorder = new MediaRecorder(stream);
    this.chunks = [];
    this.isCancelled = false;

    // Collect audio data
    this.mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) this.chunks.push(e.data);
    };

    // When recording stops, upload if not cancelled
    this.mediaRecorder.onstop = () => {
      if (this.isCancelled) return;

      const blob = new Blob(this.chunks, { type: "audio/webm" });
      this.uploadFile(blob);
    };

    this.mediaRecorder.start();
  }

  // --- 4. DIRECT UPLOAD (THE CRITICAL FIX) ---
  uploadFile(blob) {
    const file = new File([blob], "voice_note.webm", { type: "audio/webm" });
    const url = this.fileInputTarget.dataset.directUploadUrl;
    const upload = new DirectUpload(file, url);

    // UI feedback
    this.timerTarget.innerText = "...";

    upload.create((error, blob) => {
      if (error) {
        console.error("DirectUpload failed:", error);
        this.timerTarget.innerText = "Error";
      } else {
        // Success! Create the hidden input for Rails
        const hiddenField = document.createElement("input");
        hiddenField.setAttribute("type", "hidden");
        hiddenField.setAttribute("value", blob.signed_id);
        hiddenField.name = this.fileInputTarget.name;

        // IMPORTANT: Append to the form, not the controller element
        this.fileInputTarget.form.appendChild(hiddenField);

        // Submit the form via Turbo
        this.fileInputTarget.form.requestSubmit();

        // Note: UI reset happens via turbo:submit-end event in HTML
      }
    });
  }

  // --- 5. WHATSAPP STYLE VISUALIZER ---
  visualize(stream) {
    // Ensure AudioContext is active (needed for mobile safari/chrome sometimes)
    if (!this.audioCtx) {
      this.audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (this.audioCtx.state === "suspended") this.audioCtx.resume();

    const source = this.audioCtx.createMediaStreamSource(stream);
    const analyser = this.audioCtx.createAnalyser();
    // fftSize determines bar count. 256 = 128 bars (good balance for slim but visible)
    analyser.fftSize = 256;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    source.connect(analyser);

    // Canvas setup
    const canvas = this.visualizerTarget;
    const ctx = canvas.getContext("2d");
    const width = canvas.width;
    const height = canvas.height;
    const centerY = height / 2; // Middle line for mirroring

    // Create Gradient: tailwind bg-linear-tr from-green-600 to-emerald-600
    // (Bottom-Left to Top-Right)
    const gradient = ctx.createLinearGradient(0, height, width, 0);
    gradient.addColorStop(0, "#16a34a"); // green-600
    gradient.addColorStop(1, "#059669"); // emerald-600
    ctx.fillStyle = gradient;

    const draw = () => {
      // Stop drawing if stream ended
      if (!this.stream || !this.stream.active) return;

      this.visualizerAnimationId = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);

      ctx.clearRect(0, 0, width, height);

      // Calculate bar width allowing for a small gap
      const barWidth = (width / bufferLength) * 0.8;
      let barHeight;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        // Scale data (0-255) to canvas height, multiply by 0.7 to keep it from maxing out too easily
        barHeight = (dataArray[i] / 255) * height * 0.7;

        // WhatsApp Style: Mirrored vertically from center
        const y = centerY - barHeight / 2;

        // Draw bar, subtracting 1px from width for a clean gap
        ctx.fillRect(x, y, barWidth - 0.5, barHeight);

        x += width / bufferLength;
      }
    };
    draw();
  }

  // --- 6. TIMERS & CLEANUP ---
  startTimer() {
    let seconds = 0;
    this.timerTarget.innerText = "00:00";
    //Clear any existing interval just in case
    if (this.timerInterval) clearInterval(this.timerInterval);

    this.timerInterval = setInterval(() => {
      seconds++;
      const m = Math.floor(seconds / 60)
        .toString()
        .padStart(2, "0");
      const s = (seconds % 60).toString().padStart(2, "0");
      this.timerTarget.innerText = `${m}:${s}`;
    }, 1000);
  }

  cleanup() {
    if (this.timerInterval) clearInterval(this.timerInterval);
    if (this.visualizerAnimationId)
      cancelAnimationFrame(this.visualizerAnimationId);
    // Stop all audio tracks to release microphone hardware
    if (this.stream) this.stream.getTracks().forEach((t) => t.stop());
  }

  resetUI() {
    this.audioControlsTarget.classList.add("hidden");
    this.textInputTarget.disabled = false;
    this.textInputTarget.value = "";
    this.toggleSubmitBtn();
    // Clear canvas
    const ctx = this.visualizerTarget.getContext("2d");
    ctx.clearRect(
      0,
      0,
      this.visualizerTarget.width,
      this.visualizerTarget.height,
    );
  }
}
